import axios from 'axios'
import { getDeviceFingerprint } from '@/lib/fingerprint'

const API_BASE = import.meta.env.VITE_API_BASE || '/api'

const api = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    if (error.response?.status === 401) {
      // 尝试从 localStorage 获取 device_id
      let deviceId = localStorage.getItem('device_id')
      
      // 如果 localStorage 中没有 device_id，则生成指纹
      if (!deviceId) {
        try {
          deviceId = await getDeviceFingerprint()
          localStorage.setItem('device_id', deviceId)
        } catch (e) {
          console.error('Failed to generate device fingerprint:', e)
          // 生成失败时使用随机 UUID 作为兜底
          deviceId = crypto.randomUUID ? crypto.randomUUID() : 
                    'temp-' + Math.random().toString(36).substring(2)
          localStorage.setItem('device_id', deviceId)
        }
      }

      if (deviceId) {
        try {
          const res = await axios.post(`${API_BASE}/auth/register`, { deviceId })
          const data = res.data
          if (data.code === 0 && data.data.token) {
            localStorage.setItem('auth_token', data.data.token)
            error.config.headers.Authorization = `Bearer ${data.data.token}`
            return api.request(error.config)
          }
        } catch (e) {
          console.error('Token refresh failed:', e)
        }
      }
    }
    return Promise.reject(error)
  }
)

export interface ApiResponse<T = unknown> {
  code: number
  data: T
  message: string
}

// Auth
export const authApi = {
  register: (deviceId: string) => api.post<ApiResponse<{ token: string; user: { id: number; nickname: string } }>>('/auth/register', { deviceId }),
}

// Records
export interface ActivityRecord {
  id: number
  user_id: number
  category: string
  start_time: number
  end_time: number
  duration: number
  is_auto_closed: boolean
  details: Record<string, string> | null
  created_at: string
}

export const recordsApi = {
  get: (params: { startDate?: string; endDate?: string; category?: string }) =>
    api.get<ApiResponse<ActivityRecord[]>>('/records', { params }),
  create: (data: { category: string; start_time: number; end_time: number; duration: number; details?: Record<string, string> }) =>
    api.post<ApiResponse<ActivityRecord>>('/records', data),
  createBatch: (records: Array<{ category: string; start_time: number; end_time: number; duration: number; details?: Record<string, string> }>) =>
    api.post<ApiResponse<{ success: number; failed: number }>>('/records/batch', { records }),
  update: (id: number, data: { details?: Record<string, string>; duration?: number }) =>
    api.put<ApiResponse<ActivityRecord>>(`/records/${id}`, data),
  delete: (id: number) =>
    api.delete<ApiResponse<null>>(`/records/${id}`),
  stats: (period: 'day' | 'week' | 'month' | 'year') =>
    api.get<ApiResponse<Record<string, unknown>>>('/records/stats', { params: { period } }),
}

// Users
export interface UserProfile {
  id: number
  nickname: string
  device_id: string
  theme: string
  created_at: string
}

export const usersApi = {
  getProfile: () => api.get<ApiResponse<UserProfile>>('/users/profile'),
  updateNickname: (nickname: string) => api.put<ApiResponse<null>>('/users/nickname', { nickname }),
  updateSettings: (settings: { theme?: string }) => api.put<ApiResponse<null>>('/users/settings', settings),
  clearData: () => api.delete<ApiResponse<null>>('/users/data'),
  exportData: () => api.get<ApiResponse<{ records: ActivityRecord[] }>>('/users/export'),
  checkNickname: (nickname: string) => api.get<ApiResponse<{ available: boolean }>>('/users/check-nickname', { params: { nickname } }),
}

// Leaderboard
export interface LeaderboardEntry {
  rank: number
  user_id: number
  nickname: string
  value: number
  is_self: boolean
}

export const leaderboardApi = {
  duration: (period: 'week' | 'month' | 'year') =>
    api.get<ApiResponse<{ entries: LeaderboardEntry[]; myEntry: LeaderboardEntry | null }>>('/leaderboard/duration', { params: { period } }),
  streak: () =>
    api.get<ApiResponse<{ entries: LeaderboardEntry[]; myEntry: LeaderboardEntry | null }>>('/leaderboard/streak'),
}

// Timers
export const timersApi = {
  start: (data: { category: string; start_time: number }) =>
    api.post<ApiResponse<null>>('/timers/start', data),
  stop: () => api.post<ApiResponse<null>>('/timers/stop'),
  check: () => api.get<ApiResponse<{ active: boolean; timer?: { category: string; start_time: number } | null }>>('/timers/check'),
}

export default api
